/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  getStoredAccessToken,
  isTokenExpired,
  refreshAccessToken,
  clearTokens,
  redirectToSpotifyLogin,
  getTokenExpiryMs,
} from "@/lib/spotify/auth";
import { SPOTIFY_CLIENT_ID } from "@/lib/spotify/constants";

interface SpotifyAuthContextValue {
  isAuthenticated: boolean;
  accessToken: string | null;
  login: () => Promise<void>;
  logout: () => void;
  getValidToken: () => Promise<string>;
  error: string | null;
}

const SpotifyAuthContext = createContext<SpotifyAuthContextValue | null>(null);

export const SpotifyAuthProvider = ({ children }: { children: ReactNode }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    const msUntilExpiry = getTokenExpiryMs();
    // Refresh 5 minutes before expiry
    const refreshIn = Math.max(msUntilExpiry - 5 * 60 * 1000, 0);

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const data = await refreshAccessToken();
        setAccessToken(data.access_token);
        setIsAuthenticated(true);
        scheduleRefresh();
      } catch {
        setAccessToken(null);
        setIsAuthenticated(false);
        setError("Session expired. Please reconnect.");
      }
    }, refreshIn);
  }, []);

  // On mount: check for existing valid token
  useEffect(() => {
    if (!SPOTIFY_CLIENT_ID) return;

    const token = getStoredAccessToken();
    if (token && !isTokenExpired()) {
      setAccessToken(token);
      setIsAuthenticated(true);
      scheduleRefresh();
    } else if (token) {
      // Token exists but expired — try refresh
      refreshAccessToken()
        .then((data) => {
          setAccessToken(data.access_token);
          setIsAuthenticated(true);
          scheduleRefresh();
        })
        .catch(() => {
          clearTokens();
          setIsAuthenticated(false);
        });
    }

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [scheduleRefresh]);

  const login = useCallback(async () => {
    setError(null);
    await redirectToSpotifyLogin();
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setAccessToken(null);
    setIsAuthenticated(false);
    setError(null);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
  }, []);

  const getValidToken = useCallback(async (): Promise<string> => {
    const token = getStoredAccessToken();
    if (token && !isTokenExpired()) return token;

    const data = await refreshAccessToken();
    setAccessToken(data.access_token);
    setIsAuthenticated(true);
    scheduleRefresh();
    return data.access_token;
  }, [scheduleRefresh]);

  // Called by SpotifyCallback after token exchange
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.access_token) {
        setAccessToken(detail.access_token);
        setIsAuthenticated(true);
        setError(null);
        scheduleRefresh();
      }
    };
    window.addEventListener("spotify:auth-complete", handler);
    return () => window.removeEventListener("spotify:auth-complete", handler);
  }, [scheduleRefresh]);

  return (
    <SpotifyAuthContext.Provider
      value={{ isAuthenticated, accessToken, login, logout, getValidToken, error }}
    >
      {children}
    </SpotifyAuthContext.Provider>
  );
};

export function useSpotifyAuth(): SpotifyAuthContextValue {
  const ctx = useContext(SpotifyAuthContext);
  if (!ctx) throw new Error("useSpotifyAuth must be used within SpotifyAuthProvider");
  return ctx;
}
